import { FileCheck, FileQuestion, FileX, LucideIcon } from 'lucide-react';

import Icon from '@/components/Icon.tsx';

import FormattedForm from '@/models/forms/FormattedForm.ts';
import { FormsResolution } from '@/models/forms/forms-fetch.ts';

import { cn } from '@/utils/styleUtils.ts';

interface FormsIconState {
    icon: LucideIcon;
    color: string;
    label: string;
}

interface Props {
    formsResolution: FormsResolution;
    formattedFormsForAppointment: FormattedForm[] | undefined;
    /** Tailwind size utilities, for cards the default does not suit. */
    size?: string;
    /** Replaces the semantic colour, for surfaces that paint every icon alike. */
    colorClassName?: string;
}

const LOADING: FormsIconState = {
    icon: FileQuestion,
    color: 'text-slate-300',
    label: 'Formularbedarf wird geladen',
};
const UNRESOLVED: FormsIconState = {
    icon: FileQuestion,
    color: 'text-slate-400',
    label: 'Formularbedarf konnte nicht ermittelt werden',
};
const COMPLETED: FormsIconState = { icon: FileCheck, color: 'text-green-700', label: 'Alle Formulare ausgefüllt' };
const INCOMPLETE: FormsIconState = { icon: FileX, color: 'text-red-700', label: 'Formulare fehlen' };

const resolveIconState = (
    formsResolution: FormsResolution,
    formattedFormsForAppointment: FormattedForm[] | undefined
): FormsIconState => {
    // The room-view card wraps this in no spinner, so a failure label here would be announced on every
    // card of a first load.
    if (formsResolution === FormsResolution.LOADING) {
        return LOADING;
    }

    if (formsResolution !== FormsResolution.RESOLVED || !formattedFormsForAppointment) {
        return UNRESOLVED;
    }

    return formattedFormsForAppointment.every((form) => form.is_completed) ? COMPLETED : INCOMPLETE;
};

/** Red and green each assert something, so neither may show while the required set is unknown. */
export const FormsStatusIcon = ({ formsResolution, formattedFormsForAppointment, size, colorClassName }: Props) => {
    if (formsResolution === FormsResolution.NOT_APPLICABLE) {
        return undefined;
    }

    const state = resolveIconState(formsResolution, formattedFormsForAppointment);

    return (
        <Icon icon={state.icon} size={size} className={cn(colorClassName ?? state.color)} aria-label={state.label} />
    );
};
